import { ArrowBackIosNew, ArrowForwardIos, Favorite } from "@mui/icons-material";
import { Card, CardActionArea, CardMedia, CardContent, Box, Typography, IconButton, useMediaQuery } from "@mui/material";
import type { IRanking, ISavedRecipes } from "../types/recipe";
import { getRankingAverage } from "../utils/helpers/recipeHelper";
import { StarRating } from "./StarRating";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import Theme from "../utils/theme";
import { useSwipeNavigation } from "../hooks/useSwipeNavigation";

interface IThumbnailStackProps {
    recipes: ISavedRecipes[];
    currentIndex: number;
    setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
    clickHandler: (recipeId: string) => void;
}

const ThumbnailStack: React.FC<IThumbnailStackProps> = ({ recipes, currentIndex, setCurrentIndex, clickHandler }) => {
    const authState = useSelector((state: RootState) => state.auth);
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    const smUp = useMediaQuery(theme.breakpoints.up("md"));

    const { handleTouchStart, handleTouchMove, handleTouchEnd } = useSwipeNavigation({
        currentIndex: currentIndex,
        maxIndex: recipes.length - 1,
        onChange: setCurrentIndex,
    });

    const handleThumbnailClick = useCallback(
        (offset: number, index: number) => {
            if (offset !== 0) {
                setCurrentIndex(index);
            } else {
                clickHandler(recipes[index]._id);
            }
        },
        [clickHandler, recipes, setCurrentIndex]
    );

    const renderThumbnails = useMemo(() => {
        return (
            <Box
                sx={{
                    position: "relative",
                    width: { xs: 240, sm: 390, md: 640 },
                    height: smUp ? 500 : 320,
                    mx: "auto",
                    mb: 1,
                    overflow: "visible",
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {recipes.map((recipe, index) => {
                    const offset = index - currentIndex;
                    let style: React.CSSProperties = {};

                    if (offset === 0) {
                        style = {
                            zIndex: 3,
                            left: "50%",
                            top: "50%",
                            transform: "translate(-50%, -50%) scale(1)",
                            boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
                        };
                    } else if (Math.abs(offset) === 1) {
                        style = {
                            zIndex: 2,
                            left: offset < 0 ? "30%" : "70%",
                            top: "50%",
                            transform: "translate(-50%, -50%) scale(0.85)",
                            opacity: 0.7,
                            filter: "blur(1px)",
                        };
                    } else if (Math.abs(offset) === 2) {
                        style = {
                            zIndex: 1,
                            left: offset < 0 ? (smUp ? 0 : "10%") : smUp ? "100%" : "90%",
                            top: "50%",
                            transform: "translate(-50%, -50%) scale(0.60)",
                            opacity: 0.7,
                            filter: "blur(4px)",
                        };
                    } else {
                        style = { display: "none" };
                    }

                    const filteredRanking = recipe.ranking?.filter((rank: IRanking) => rank.user.username === authState.account?.username);
                    const starRatingValue = filteredRanking ? (filteredRanking[0] ? filteredRanking[0].value : 0) : 0;

                    return (
                        <Card
                            key={index}
                            sx={{
                                position: "absolute",
                                borderRadius: "20px",
                                display: "flex",
                                flexDirection: "column",
                                width: "100%",
                                height: smUp ? 500 : 340,
                                objectFit: "cover",
                                ...style,
                            }}
                        >
                            <CardActionArea
                                onClick={() => handleThumbnailClick(offset, index)}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    height: "100%",
                                    alignItems: "stretch",
                                }}
                            >
                                <CardMedia
                                    component="img"
                                    height={smUp ? "400" : "240"}
                                    image={recipe.image?.url || "/images/recipePlaceholder.png"}
                                    alt={recipe.name}
                                    sx={{ width: "100%" }}
                                />
                                <CardContent
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        flexGrow: 1,
                                    }}
                                >
                                    <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", width: "100%" }}>
                                        <Typography variant="subtitle2" sx={{ fontSize: smUp ? "18px" : "default", textAlign: "center" }}>
                                            {recipe.name}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "flex-end",
                                            mt: 1,
                                        }}
                                    >
                                        <StarRating
                                            size={"sm"}
                                            objectId={recipe._id}
                                            average={recipe.ranking ? getRankingAverage(recipe.ranking) : 0}
                                            count={recipe.ranking?.length}
                                            value={starRatingValue ?? 0}
                                        />
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <Favorite sx={{ fontSize: 24, color: theme.palette.primary.main }} fontSize="small" />
                                            <Typography variant="subtitle2" sx={{ fontSize: 16 }}>
                                                {recipe.likes?.length}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    );
                })}
                {currentIndex > 0 && (
                    <IconButton
                        onClick={() => setCurrentIndex(currentIndex - 1)}
                        sx={{
                            position: "absolute",
                            left: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 4,
                            background: "rgba(255,255,255,0.7)",
                            "&:hover": {
                                background: "rgba(255,255,255,0.7)",
                            },
                        }}
                    >
                        <ArrowBackIosNew sx={{ color: theme.palette.primary.main }} />
                    </IconButton>
                )}
                {currentIndex < recipes.length - 1 && (
                    <IconButton
                        onClick={() => setCurrentIndex(currentIndex + 1)}
                        sx={{
                            position: "absolute",
                            right: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 4,
                            background: "rgba(255,255,255,0.7)",
                            "&:hover": {
                                background: "rgba(255,255,255,0.7)",
                            },
                        }}
                    >
                        <ArrowForwardIos sx={{ color: theme.palette.primary.main }} />
                    </IconButton>
                )}
            </Box>
        );
    }, [
        authState.account?.username,
        currentIndex,
        handleThumbnailClick,
        handleTouchEnd,
        handleTouchMove,
        handleTouchStart,
        recipes,
        setCurrentIndex,
        smUp,
        theme.palette.primary.main,
    ]);

    return <>{renderThumbnails}</>;
};

export default ThumbnailStack;
